import React, { useState, useRef } from "react";

/**
 * Générateur de certificats d'adhésion en masse.
 * - Colle la liste (CSV) ou importe un fichier .csv
 * - Génère un ZIP téléchargeable OU envoie chaque certificat par email
 */
export default function CertificatsBulkAdminPanel({ API, pinInput }) {
  const [raw, setRaw] = useState(
    "# Nom ; N° ; Ville ; Date ; Email ; Téléphone (pour WhatsApp)\n" +
    "APEDO Ablavi ; SAF-2026-001 ; Atakpamé ; 27/06/2026 ; ablavi.apedo@example.com ; +228 91 14 25 19\n" +
    "DOGLO Komi ; SAF-2026-002 ; Atakpamé ; 27/06/2026 ; doglo.komi@example.com ; +228 90 12 34 56\n" +
    "ABOTCHI Émilie Djifa ; SAF-2026-003 ; Atakpamé ; 27/06/2026 ; emilie.abotchi@example.com ; +228 92 33 44 55"
  );
  const [busy, setBusy] = useState(false);
  const [sendEmail, setSendEmail] = useState(false);
  const [ccAdmin, setCcAdmin] = useState(true);
  const [msg, setMsg] = useState("");
  const [msgType, setMsgType] = useState("info");
  const [emailReport, setEmailReport] = useState(null);
  const [waReport, setWaReport] = useState(null);
  const [twilioReport, setTwilioReport] = useState(null);
  const [broadcastReport, setBroadcastReport] = useState(null);
  const [broadcastDoc, setBroadcastDoc] = useState("rapport-odon-itsou-2026");
  const [reminderYear, setReminderYear] = useState("2027");
  const [translateBusy, setTranslateBusy] = useState(false);
  const [translateResult, setTranslateResult] = useState(null);
  const fileInputRef = useRef(null);

  const pin = pinInput || "SAFUAYE2024";

  const translateDocument = async (targetLang) => {
    const langLabel = targetLang === "en" ? "anglais 🇬🇧" : "italien 🇮🇹";
    if (!window.confirm(
      `Traduire « ${broadcastDoc} » en ${langLabel} avec Gemini IA ?\n\n` +
      `⚠️ Traduction assistée par IA — informative, non contractuelle.\n` +
      `Génère un PDF avec disclaimer légal. Peut prendre 20-40 secondes.`
    )) return;
    try {
      setTranslateBusy(true);
      setTranslateResult(null);
      const res = await fetch(`${API}/ai/translate-document`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ pin, document_key: broadcastDoc, target_lang: targetLang }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Erreur");
      setTranslateResult(data);
      setMsg(`✅ Traduction ${targetLang.toUpperCase()} générée : ${data.output_filename}`);
      setMsgType("success");
    } catch (e) {
      setMsg("❌ Erreur traduction : " + (e.message || "inconnue"));
      setMsgType("error");
    } finally {
      setTranslateBusy(false);
    }
  };

  const _parse = (text) => {
    const lines = text.split("\n").map((l) => l.trim())
      .filter((l) => l && !l.startsWith("#"));
    return lines.map((line, i) => {
      const parts = line.split(/\s*[;,\t]\s*/);
      return {
        nom: parts[0] || "",
        numero: parts[1] || `SAF-2026-${String(i + 1).padStart(3, "0")}`,
        ville: parts[2] || "Atakpamé",
        date_adhesion: parts[3] || "",
        email: parts[4] || "",
        telephone: parts[5] || "",
      };
    }).filter((it) => it.nom);
  };

  const preview = _parse(raw);
  const withEmail = preview.filter((p) => p.email && p.email.includes("@")).length;
  const missingEmail = preview.length - withEmail;

  const handleImportCsv = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      // Normalise : détecte virgule ou point-virgule, prend colonnes
      // Nom, Numéro, Ville, Date, Email (les 5 premières)
      const lines = text.split(/\r?\n/).filter((l) => l.trim());
      if (lines.length === 0) {
        setMsg("Fichier CSV vide.");
        setMsgType("error");
        return;
      }
      // Auto-détecte séparateur : privilégie ; sinon ,
      const sep = lines[0].includes(";") ? ";" : ",";
      // Si première ligne semble être un en-tête (contient "nom" ou "email"),
      // la préserver en commentaire
      const firstLower = lines[0].toLowerCase();
      const isHeader = firstLower.includes("nom") || firstLower.includes("name") ||
                       firstLower.includes("email");
      const dataLines = isHeader ? lines.slice(1) : lines;

      const normalised = dataLines.map((line) => {
        // Enlever les guillemets Excel
        const parts = line.split(sep).map((p) => p.trim().replace(/^["']|["']$/g, ""));
        return parts.slice(0, 5).join(" ; ");
      });
      const header = "# Importé depuis " + file.name + " (" + normalised.length + " lignes)";
      setRaw([header, ...normalised].join("\n"));
      setMsg(`✅ ${normalised.length} lignes importées depuis ${file.name}`);
      setMsgType("success");
    } catch (err) {
      setMsg(`❌ Erreur lecture CSV : ${err.message}`);
      setMsgType("error");
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const generate = async () => {
    if (!pinInput) {
      setMsg("PIN administrateur manquant.");
      setMsgType("error");
      return;
    }
    const adherents = _parse(raw);
    if (adherents.length === 0) {
      setMsg("Aucun adhérent valide dans la liste.");
      setMsgType("error");
      return;
    }
    if (sendEmail) {
      const withoutEmail = adherents.filter((a) => !a.email || !a.email.includes("@"));
      if (withoutEmail.length > 0) {
        if (!window.confirm(
          `${withoutEmail.length} adhérent(s) n'ont pas d'email valide et seront ignorés.\n\nContinuer avec les ${adherents.length - withoutEmail.length} envois valides ?`
        )) return;
      }
    }
    setBusy(true);
    setEmailReport(null);
    setMsg(sendEmail
      ? `📧 Envoi en cours à ${adherents.length} adhérent(s)…`
      : `⏳ Génération de ${adherents.length} certificat(s)…`);
    setMsgType("info");
    try {
      const res = await fetch(`${API}/certificats/bulk-generate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          pin: pinInput, adherents,
          send_email: sendEmail, cc_admin: ccAdmin,
        }),
      });
      if (!res.ok) {
        const err = await res.text();
        throw new Error(err || `HTTP ${res.status}`);
      }
      if (sendEmail) {
        const report = await res.json();
        setEmailReport(report);
        setMsg(`✅ Envoi terminé — ${report.sent}/${report.total} certificats envoyés par email.`);
        setMsgType(report.failed > 0 ? "info" : "success");
      } else {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        const cd = res.headers.get("Content-Disposition") || "";
        const match = cd.match(/filename="?([^"]+)"?/);
        a.download = match ? match[1] : `SAFUAYE_Certificats_${Date.now()}.zip`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
        setMsg(`✅ ZIP téléchargé : ${adherents.length} certificat(s) générés.`);
        setMsgType("success");
      }
    } catch (e) {
      setMsg(`❌ Erreur : ${e.message}`);
      setMsgType("error");
    } finally {
      setBusy(false);
    }
  };

  const generateRegistre = async () => {
    if (!pinInput) {
      setMsg("PIN administrateur manquant."); setMsgType("error"); return;
    }
    const adherents = _parse(raw);
    if (adherents.length === 0) {
      setMsg("Aucun adhérent valide dans la liste."); setMsgType("error"); return;
    }
    setBusy(true); setEmailReport(null);
    setMsg(`⏳ Génération du registre officiel (${adherents.length} lignes)…`);
    setMsgType("info");
    try {
      const res = await fetch(`${API}/registre/generate-from-list`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ pin: pinInput, adherents }),
      });
      if (!res.ok) throw new Error(await res.text() || `HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const cd = res.headers.get("Content-Disposition") || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      a.download = m ? m[1] : `SAFUAYE_Registre_${Date.now()}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      setMsg(`✅ Registre officiel généré et téléchargé (${adherents.length} lignes pré-remplies).`);
      setMsgType("success");
    } catch (e) {
      setMsg(`❌ Erreur : ${e.message}`);
      setMsgType("error");
    } finally { setBusy(false); }
  };

  const sendCotisationReminders = async () => {
    if (!pinInput) {
      setMsg("PIN administrateur manquant."); setMsgType("error"); return;
    }
    const adherents = _parse(raw);
    if (adherents.length === 0) {
      setMsg("Aucun adhérent valide dans la liste."); setMsgType("error"); return;
    }
    const withEmailList = adherents.filter((a) => a.email && a.email.includes("@"));
    if (withEmailList.length === 0) {
      setMsg("Aucun adhérent avec email valide dans la liste.");
      setMsgType("error");
      return;
    }
    if (!window.confirm(
      `Envoyer ${withEmailList.length} rappel(s) de cotisation ${reminderYear} ` +
      `par email ?\n\n(${adherents.length - withEmailList.length} adhérent(s) sans email seront ignorés)`
    )) return;
    setBusy(true); setEmailReport(null);
    setMsg(`📧 Envoi des rappels de cotisation ${reminderYear}…`);
    setMsgType("info");
    try {
      const res = await fetch(`${API}/cotisation/send-reminders`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          pin: pinInput, adherents,
          annee_cible: reminderYear, cc_admin: ccAdmin,
        }),
      });
      if (!res.ok) throw new Error(await res.text() || `HTTP ${res.status}`);
      const report = await res.json();
      setEmailReport(report);
      setMsg(`✅ ${report.sent}/${report.total} rappels de cotisation ${report.annee_cible} envoyés.`);
      setMsgType(report.failed > 0 ? "info" : "success");
    } catch (e) {
      setMsg(`❌ Erreur : ${e.message}`);
      setMsgType("error");
    } finally { setBusy(false); }
  };

  const generateWhatsAppLinks = async () => {
    if (!pinInput) {
      setMsg("PIN administrateur manquant."); setMsgType("error"); return;
    }
    const adherents = _parse(raw);
    if (adherents.length === 0) {
      setMsg("Aucun adhérent valide dans la liste."); setMsgType("error"); return;
    }
    setBusy(true); setEmailReport(null); setWaReport(null);
    setMsg(`⏳ Génération des certificats et liens WhatsApp (${adherents.length})…`);
    setMsgType("info");
    try {
      const res = await fetch(`${API}/certificats/whatsapp-links`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ pin: pinInput, adherents }),
      });
      if (!res.ok) throw new Error(await res.text() || `HTTP ${res.status}`);
      const report = await res.json();
      setWaReport(report);
      setMsg(`✅ ${report.ready}/${report.total} liens WhatsApp prêts à envoyer. Certificats hébergés sur safuaye.org/certifs/`);
      setMsgType(report.missing_phone > 0 ? "info" : "success");
    } catch (e) {
      setMsg(`❌ Erreur : ${e.message}`);
      setMsgType("error");
    } finally { setBusy(false); }
  };

  const openAllWhatsAppLinks = () => {
    if (!waReport?.results) return;
    const ready = waReport.results.filter((r) => r.ready);
    if (ready.length === 0) return;
    if (!window.confirm(
      `Ouvrir ${ready.length} onglets WhatsApp d'affilée ?\n\n` +
      `Le navigateur peut te demander de désactiver le bloqueur de pop-ups. ` +
      `Chaque onglet ouvre WhatsApp Web avec le message prêt — il suffit de cliquer sur ` +
      `Envoyer dans chaque conversation.`
    )) return;
    ready.forEach((r, idx) => {
      setTimeout(() => window.open(r.wa_link, "_blank", "noopener"), idx * 800);
    });
  };

  const sendCertificatsTwilio = async (channel) => {
    if (!pinInput) { setMsg("PIN administrateur manquant."); setMsgType("error"); return; }
    const adherents = _parse(raw);
    if (adherents.length === 0) { setMsg("Aucun adhérent valide."); setMsgType("error"); return; }
    const withPhone = adherents.filter(
      (a) => a.telephone && a.telephone.replace(/\D/g, "").length >= 8
    );
    if (withPhone.length === 0) {
      setMsg("Aucun adhérent avec téléphone valide."); setMsgType("error"); return;
    }
    if (!window.confirm(
      `Envoyer les certificats via ${channel === "whatsapp" ? "WhatsApp (avec PDF joint)" : "SMS (avec lien PDF)"} ` +
      `à ${withPhone.length} adhérent(s) ?\n\n` +
      `⚠️ Twilio doit être configuré dans backend/.env (TWILIO_ACCOUNT_SID, AUTH_TOKEN, ` +
      `TWILIO_${channel === "whatsapp" ? "WHATSAPP" : "SMS"}_FROM).`
    )) return;
    setBusy(true); setEmailReport(null); setWaReport(null); setTwilioReport(null);
    setMsg(`📲 Envoi Twilio ${channel.toUpperCase()} en cours (${withPhone.length})…`);
    setMsgType("info");
    try {
      const res = await fetch(`${API}/twilio/send-certificats`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ pin: pinInput, adherents, channel }),
      });
      if (!res.ok) throw new Error(await res.text() || `HTTP ${res.status}`);
      const report = await res.json();
      setTwilioReport(report);
      setMsg(`✅ ${report.sent}/${report.total} certificats envoyés via ${channel.toUpperCase()} (Twilio).`);
      setMsgType(report.failed > 0 ? "info" : "success");
    } catch (e) {
      setMsg(`❌ Erreur : ${e.message}`); setMsgType("error");
    } finally { setBusy(false); }
  };

  const sendBroadcastReport = async () => {
    if (!pinInput) { setMsg("PIN administrateur manquant."); setMsgType("error"); return; }
    const adherents = _parse(raw);
    // Pour la diffusion, on utilise nom + email (les Sages sont dans la même liste CSV)
    const recipients = adherents
      .filter((a) => a.email && a.email.includes("@"))
      .map((a) => ({
        nom: a.nom,
        email: a.email,
        titre: a.ville && a.ville !== "Atakpamé" ? a.ville : "Sage / Membre d'honneur",
      }));
    if (recipients.length === 0) {
      setMsg("Aucun destinataire avec email valide dans la liste.");
      setMsgType("error"); return;
    }
    const docLabels = {
      "rapport-odon-itsou-2026": "Rapport Photo Odôn-Itsou 2026",
      "bilan-odon-itsou-original": "Bilan Odôn-Itsou (original)",
    };
    if (!window.confirm(
      `Envoyer le document « ${docLabels[broadcastDoc]} » par email à ${recipients.length} destinataire(s) ?\n\n` +
      `Ils recevront le PDF officiel + un message de gratitude signé du Bureau Exécutif.\n\n` +
      `(Adhérents sans email : ${adherents.length - recipients.length} — ignorés)`
    )) return;
    setBusy(true); setBroadcastReport(null); setEmailReport(null); setWaReport(null); setTwilioReport(null);
    setMsg(`📄 Diffusion « ${docLabels[broadcastDoc]} » en cours (${recipients.length})…`);
    setMsgType("info");
    try {
      const res = await fetch(`${API}/broadcast/send`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          pin: pinInput,
          document_key: broadcastDoc,
          recipients,
          cc_admin: ccAdmin,
        }),
      });
      if (!res.ok) throw new Error(await res.text() || `HTTP ${res.status}`);
      const report = await res.json();
      setBroadcastReport(report);
      setMsg(`✅ ${report.sent}/${report.total} envois réussis pour « ${report.document_title} ».`);
      setMsgType(report.failed > 0 ? "info" : "success");
    } catch (e) {
      setMsg(`❌ Erreur : ${e.message}`); setMsgType("error");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="certificats-bulk-panel">
      <h3 className="font-heading text-xl font-bold text-gray-900 mb-2">
        🏅 Certificats d&apos;Adhésion — Génération en masse
      </h3>
      <p className="text-sm text-gray-600 mb-4">
        Colle la liste ou importe un fichier CSV (une ligne par adhérent).
        <br />
        <span className="font-mono text-xs text-gray-700 bg-gray-50 px-2 py-0.5 rounded">
          Nom complet ; N° SAF-2026-XXX ; Ville ; Date JJ/MM/AAAA ; Email ; Téléphone
        </span>
        <br />
        Le <b>téléphone</b> est utilisé pour l&apos;envoi via WhatsApp (format +228 XX XX XX XX ou 8 chiffres).
        L&apos;<b>email</b> est requis pour l&apos;envoi automatique par mail.
      </p>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3 mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,text/csv,text/plain"
          onChange={handleImportCsv}
          className="hidden"
          data-testid="certificats-bulk-csv-input"
        />
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={busy}
          data-testid="certificats-bulk-import-csv"
          className="px-4 py-2 bg-white border-2 border-amber-600 text-amber-700 rounded-lg hover:bg-amber-100 font-semibold text-sm flex items-center gap-2"
        >
          📥 Importer un fichier .csv
        </button>
        <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
          <input
            type="checkbox"
            checked={sendEmail}
            onChange={(e) => setSendEmail(e.target.checked)}
            className="w-4 h-4 accent-amber-600"
            data-testid="certificats-bulk-send-email"
          />
          <b>📧 Envoyer par email</b> à chaque adhérent
        </label>
        {sendEmail && (
          <label className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer">
            <input
              type="checkbox"
              checked={ccAdmin}
              onChange={(e) => setCcAdmin(e.target.checked)}
              className="w-3.5 h-3.5 accent-amber-600"
            />
            Copie admin (associationsafuaye@gmail.com)
          </label>
        )}
        {sendEmail && (
          <span className="ml-auto text-xs text-amber-800 bg-amber-100 px-2 py-1 rounded">
            {withEmail} avec email · {missingEmail} sans email
          </span>
        )}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Colonne gauche — saisie */}
        <div>
          <label className="block text-sm font-bold text-gray-700 mb-2" htmlFor="bulk-cert-textarea">
            Liste des adhérents ({preview.length} détecté{preview.length > 1 ? "s" : ""})
          </label>
          <textarea
            id="bulk-cert-textarea"
            data-testid="certificats-bulk-textarea"
            value={raw}
            onChange={(e) => setRaw(e.target.value)}
            rows={14}
            className="w-full border-2 border-gray-300 rounded-lg px-3 py-2 font-mono text-xs focus:border-amber-600 focus:outline-none"
            placeholder="APEDO Ablavi ; SAF-2026-001 ; Atakpamé ; 27/06/2026 ; ablavi@example.com"
          />
          <div className="flex flex-wrap items-center gap-3 mt-4">
            <button
              onClick={generate}
              disabled={busy || preview.length === 0}
              data-testid="certificats-bulk-generate"
              className={`px-6 py-3 disabled:bg-gray-300 text-white font-bold rounded-lg shadow flex items-center gap-2 ${
                sendEmail ? "bg-emerald-600 hover:bg-emerald-700" : "bg-amber-600 hover:bg-amber-700"
              }`}
            >
              {busy ? "⏳ En cours…" : sendEmail
                ? `📧 Envoyer ${preview.length} email${preview.length > 1 ? "s" : ""}`
                : `📦 Générer ZIP (${preview.length} certificat${preview.length > 1 ? "s" : ""})`}
            </button>
            <button
              onClick={() => { setRaw(""); setEmailReport(null); }}
              disabled={busy}
              className="px-4 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg"
            >
              🗑️ Vider
            </button>
          </div>

          {/* Actions annexes : Registre + Rappel cotisation */}
          <div className="mt-3 pt-3 border-t border-gray-200 flex flex-wrap items-center gap-2">
            <span className="text-xs text-gray-500 font-semibold uppercase tracking-wider mr-2">
              Autres actions sur cette liste :
            </span>
            <button
              onClick={generateRegistre}
              disabled={busy || preview.length === 0}
              data-testid="certificats-bulk-generate-registre"
              className="px-4 py-2 bg-blue-700 hover:bg-blue-800 disabled:bg-gray-300 text-white text-sm font-semibold rounded-lg shadow flex items-center gap-1"
              title="Génère le Registre officiel A4 paysage pré-rempli"
            >
              📚 Générer Registre officiel
            </button>
            <button
              onClick={generateWhatsAppLinks}
              disabled={busy || preview.length === 0}
              data-testid="certificats-bulk-whatsapp"
              className="px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-300 text-white text-sm font-semibold rounded-lg shadow flex items-center gap-1"
              title="Génère les liens WhatsApp prêts à envoyer (Togo)"
            >
              📱 Liens WhatsApp
            </button>
            <button
              onClick={() => sendCertificatsTwilio("whatsapp")}
              disabled={busy || preview.length === 0}
              data-testid="certificats-bulk-twilio-whatsapp"
              className="px-4 py-2 bg-emerald-700 hover:bg-emerald-800 disabled:bg-gray-300 text-white text-sm font-semibold rounded-lg shadow flex items-center gap-1"
              title="Envoi automatique via Twilio WhatsApp (PDF joint)"
            >
              🚀 Auto WhatsApp Twilio
            </button>
            <button
              onClick={() => sendCertificatsTwilio("sms")}
              disabled={busy || preview.length === 0}
              data-testid="certificats-bulk-twilio-sms"
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white text-sm font-semibold rounded-lg shadow flex items-center gap-1"
              title="Envoi automatique via Twilio SMS (lien PDF)"
            >
              💬 Auto SMS Twilio
            </button>
            <div className="flex items-center gap-1 bg-white border border-gray-300 rounded-lg px-2 py-1">
              <span className="text-xs text-gray-600">Année :</span>
              <input
                type="text"
                value={reminderYear}
                onChange={(e) => setReminderYear(e.target.value)}
                className="w-14 text-center text-sm font-mono border-0 focus:outline-none"
                data-testid="certificats-bulk-reminder-year"
              />
            </div>
            <button
              onClick={sendCotisationReminders}
              disabled={busy || preview.length === 0}
              data-testid="certificats-bulk-send-reminders"
              className="px-4 py-2 bg-rose-600 hover:bg-rose-700 disabled:bg-gray-300 text-white text-sm font-semibold rounded-lg shadow flex items-center gap-1"
              title="Envoie un email de rappel de cotisation à chaque adhérent"
            >
              🔔 Envoyer rappel cotisation
            </button>
          </div>

          {/* Diffusion Rapport aux Sages / Membres d'honneur */}
          <div className="mt-3 pt-3 border-t border-gray-200 flex flex-wrap items-center gap-2">
            <span className="text-xs text-gray-500 font-semibold uppercase tracking-wider mr-2">
              📄 Diffusion officielle (Sages, membres d&apos;honneur, partenaires) :
            </span>
            <select
              value={broadcastDoc}
              onChange={(e) => setBroadcastDoc(e.target.value)}
              data-testid="certificats-bulk-broadcast-select"
              className="text-sm border border-gray-300 rounded px-2 py-1.5 bg-white focus:border-indigo-600 focus:outline-none"
            >
              <option value="rapport-odon-itsou-2026">Rapport Photo Odôn-Itsou 2026 (nouveau)</option>
              <option value="bilan-odon-itsou-original">Bilan Odôn-Itsou (original)</option>
            </select>
            <button
              onClick={sendBroadcastReport}
              disabled={busy || preview.length === 0}
              data-testid="certificats-bulk-broadcast-send"
              className="px-4 py-2 bg-indigo-700 hover:bg-indigo-800 disabled:bg-gray-300 text-white text-sm font-semibold rounded-lg shadow flex items-center gap-1"
              title="Envoie le PDF choisi aux destinataires ayant un email valide"
            >
              🚀 Envoyer document PDF
            </button>
            <button
              onClick={() => translateDocument("en")}
              disabled={translateBusy}
              data-testid={`translate-en-${broadcastDoc}`}
              className="px-3 py-2 bg-fuchsia-100 hover:bg-fuchsia-200 disabled:opacity-50 text-fuchsia-900 text-sm font-semibold rounded-lg border border-fuchsia-300 flex items-center gap-1"
              title="Générer une version anglaise du document via Gemini IA (Charity Commission UK)"
            >
              ✨ 🇬🇧 EN
            </button>
            <button
              onClick={() => translateDocument("it")}
              disabled={translateBusy}
              data-testid={`translate-it-${broadcastDoc}`}
              className="px-3 py-2 bg-fuchsia-100 hover:bg-fuchsia-200 disabled:opacity-50 text-fuchsia-900 text-sm font-semibold rounded-lg border border-fuchsia-300 flex items-center gap-1"
              title="Générer une version italienne du document via Gemini IA (diaspora italienne)"
            >
              ✨ 🇮🇹 IT
            </button>
            {translateBusy && (
              <span className="text-xs text-fuchsia-700 italic animate-pulse ml-1">
                Traduction Gemini en cours…
              </span>
            )}
            {translateResult && (
              <a
                href={translateResult.public_url}
                target="_blank"
                rel="noreferrer"
                data-testid="translated-doc-link"
                className="text-xs text-emerald-700 hover:underline font-medium ml-2"
              >
                📥 Télécharger {translateResult.output_filename}
              </a>
            )}
          </div>
          {msg && (
            <div
              data-testid="certificats-bulk-status"
              className={`mt-4 p-3 rounded-lg text-sm ${
                msgType === "success" ? "bg-green-50 text-green-800 border border-green-200"
                : msgType === "error" ? "bg-red-50 text-red-800 border border-red-200"
                : "bg-blue-50 text-blue-800 border border-blue-200"
              }`}
            >
              {msg}
            </div>
          )}
        </div>

        {/* Colonne droite — aperçu OU rapport d'envoi */}
        <div>
          {broadcastReport ? (
            <>
              <label className="block text-sm font-bold text-gray-700 mb-2">
                📄 Diffusion « {broadcastReport.document_title} » — {broadcastReport.sent}/{broadcastReport.total} envoyés
              </label>
              <div className="border-2 border-indigo-200 rounded-lg overflow-hidden">
                <div className="max-h-[26rem] overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-indigo-50 sticky top-0">
                      <tr>
                        <th className="px-2 py-2 text-left">Nom</th>
                        <th className="px-2 py-2 text-left">Email</th>
                        <th className="px-2 py-2 text-center">Statut</th>
                      </tr>
                    </thead>
                    <tbody>
                      {broadcastReport.results.map((r, i) => (
                        <tr key={i} className={r.sent ? "bg-white" : "bg-red-50"}>
                          <td className="px-2 py-1.5">
                            <div className="font-medium">{r.nom}</div>
                            {r.titre && <div className="text-[10px] text-indigo-700 italic">{r.titre}</div>}
                          </td>
                          <td className="px-2 py-1.5 text-xs font-mono">{r.email}</td>
                          <td className="px-2 py-1.5 text-center">
                            {r.sent ? "✅" : "❌"}
                            <div className="text-[10px] text-gray-500 mt-0.5 truncate max-w-[180px]">{r.reason}</div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <button
                onClick={() => setBroadcastReport(null)}
                className="mt-2 text-xs text-blue-700 hover:underline"
              >← Revenir à l&apos;aperçu</button>
            </>
          ) : twilioReport ? (
            <>
              <label className="block text-sm font-bold text-gray-700 mb-2">
                🚀 Envoi Twilio {twilioReport.channel.toUpperCase()} — {twilioReport.sent}/{twilioReport.total} envoyés
              </label>
              <div className="border-2 border-emerald-200 rounded-lg overflow-hidden">
                <div className="max-h-[26rem] overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-emerald-50 sticky top-0">
                      <tr>
                        <th className="px-2 py-2 text-left">Nom</th>
                        <th className="px-2 py-2 text-left">Téléphone</th>
                        <th className="px-2 py-2 text-center">Statut</th>
                      </tr>
                    </thead>
                    <tbody>
                      {twilioReport.results.map((r, i) => (
                        <tr key={i} className={r.sent ? "bg-white" : "bg-red-50"}>
                          <td className="px-2 py-1.5">
                            <div className="font-medium">{r.nom}</div>
                            <div className="text-[10px] text-amber-700 font-mono">{r.numero}</div>
                          </td>
                          <td className="px-2 py-1.5 text-xs font-mono">{r.phone || "—"}</td>
                          <td className="px-2 py-1.5 text-center">
                            {r.sent ? "✅" : "❌"}
                            <div className="text-[10px] text-gray-500 mt-0.5 truncate max-w-[180px]">
                              {r.status || r.reason}
                            </div>
                            {r.cert_url && (
                              <a href={r.cert_url} target="_blank" rel="noreferrer"
                                 className="text-[10px] text-blue-700 hover:underline">📄 PDF</a>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                💡 Les statuts de livraison (delivered, read, failed) sont mis à jour
                automatiquement via le webhook Twilio (visibles dans l&apos;onglet
                <b> Notifications</b>).
              </p>
              <button
                onClick={() => setTwilioReport(null)}
                className="mt-2 text-xs text-blue-700 hover:underline"
              >
                ← Revenir à l&apos;aperçu
              </button>
            </>
          ) : waReport ? (
            <>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-bold text-gray-700">
                  Liens WhatsApp ({waReport.ready} prêts · {waReport.missing_phone} sans numéro)
                </label>
                <button
                  onClick={openAllWhatsAppLinks}
                  disabled={waReport.ready === 0}
                  data-testid="certificats-bulk-whatsapp-open-all"
                  className="text-xs bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded font-semibold disabled:bg-gray-300"
                >
                  📤 Ouvrir tout ({waReport.ready})
                </button>
              </div>
              <div className="border-2 border-green-200 rounded-lg overflow-hidden">
                <div className="max-h-[26rem] overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-green-50 sticky top-0">
                      <tr>
                        <th className="px-2 py-2 text-left">Nom</th>
                        <th className="px-2 py-2 text-left">Téléphone</th>
                        <th className="px-2 py-2 text-center">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {waReport.results.map((r, i) => (
                        <tr key={i} className={r.ready ? "bg-white" : "bg-red-50"}>
                          <td className="px-2 py-1.5">
                            <div className="font-medium">{r.nom}</div>
                            <div className="text-[10px] text-amber-700 font-mono">{r.numero}</div>
                          </td>
                          <td className="px-2 py-1.5 text-xs font-mono">
                            {r.telephone || <span className="text-gray-400">—</span>}
                          </td>
                          <td className="px-2 py-1.5 text-center">
                            {r.ready ? (
                              <a
                                href={r.wa_link}
                                target="_blank"
                                rel="noreferrer"
                                data-testid={`certificats-bulk-whatsapp-link-${i}`}
                                className="inline-block px-3 py-1 bg-green-600 hover:bg-green-700 text-white rounded text-xs font-semibold"
                              >
                                📱 WhatsApp
                              </a>
                            ) : (
                              <span className="text-[10px] text-red-600 italic">{r.reason}</span>
                            )}
                            <div className="mt-1">
                              <a
                                href={r.cert_url}
                                target="_blank"
                                rel="noreferrer"
                                className="text-[10px] text-blue-700 hover:underline"
                              >
                                📄 PDF
                              </a>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                💡 Cliquer sur <b>WhatsApp</b> ouvre l&apos;app avec le message prêt et le lien PDF —
                il suffit d&apos;appuyer sur Envoyer. Les PDF restent hébergés sur <code>safuaye.org/certifs/</code>.
              </p>
              <button
                onClick={() => setWaReport(null)}
                className="mt-2 text-xs text-blue-700 hover:underline"
              >
                ← Revenir à l&apos;aperçu
              </button>
            </>
          ) : emailReport ? (
            <>
              <label className="block text-sm font-bold text-gray-700 mb-2">
                Rapport d&apos;envoi ({emailReport.sent} succès · {emailReport.failed} échecs)
              </label>
              <div className="border-2 border-gray-200 rounded-lg overflow-hidden">
                <div className="max-h-[26rem] overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-100 sticky top-0">
                      <tr>
                        <th className="px-2 py-2 text-left">Nom</th>
                        <th className="px-2 py-2 text-left">Email</th>
                        <th className="px-2 py-2 text-center">Statut</th>
                      </tr>
                    </thead>
                    <tbody>
                      {emailReport.results.map((r, i) => (
                        <tr key={i} className={r.sent ? "bg-green-50" : "bg-red-50"}>
                          <td className="px-2 py-1.5 font-medium">{r.nom}</td>
                          <td className="px-2 py-1.5 text-xs font-mono">{r.email || "—"}</td>
                          <td className="px-2 py-1.5 text-center">
                            {r.sent ? "✅" : "❌"}
                            <div className="text-[10px] text-gray-500 mt-0.5">{r.reason}</div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <button
                onClick={() => setEmailReport(null)}
                className="mt-3 text-xs text-blue-700 hover:underline"
              >
                ← Revenir à l&apos;aperçu
              </button>
            </>
          ) : (
            <>
              <label className="block text-sm font-bold text-gray-700 mb-2">
                Aperçu de la liste parsée
              </label>
              <div className="border-2 border-gray-200 rounded-lg overflow-hidden">
                <div className="max-h-[26rem] overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-100 sticky top-0">
                      <tr>
                        <th className="px-2 py-2 text-left">Nom</th>
                        <th className="px-2 py-2 text-left">N°</th>
                        <th className="px-2 py-2 text-left">Ville</th>
                        <th className="px-2 py-2 text-left">Email</th>
                        <th className="px-2 py-2 text-left">Tél.</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.length === 0 && (
                        <tr>
                          <td colSpan={5} className="px-3 py-6 text-center text-gray-400 italic">
                            Aucun adhérent à afficher.
                          </td>
                        </tr>
                      )}
                      {preview.map((it, i) => {
                        const emailOk = it.email && it.email.includes("@");
                        const phoneOk = it.telephone && it.telephone.replace(/\D/g, "").length >= 8;
                        return (
                          <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                            <td className="px-2 py-1.5 font-medium">{it.nom}</td>
                            <td className="px-2 py-1.5 text-xs text-amber-700 font-mono">{it.numero}</td>
                            <td className="px-2 py-1.5 text-xs">{it.ville}</td>
                            <td className={`px-2 py-1.5 text-xs font-mono ${emailOk ? "text-green-700" : "text-gray-400"}`}>
                              {it.email || "—"}
                            </td>
                            <td className={`px-2 py-1.5 text-xs font-mono ${phoneOk ? "text-green-700" : "text-gray-400"}`}>
                              {it.telephone || "—"}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-3">
                💡 Le certificat inclut la <b>signature du Président AZIAKA KOFFI</b>,
                le QR code de vérification et la mission élargie de l&apos;association.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
